// Countertops/Cabinets manual estimate — pure helpers for the OrderConfirmation-shaped material &
// labor item model. The C/C quantity page renders the same Material/Labor tables as
// OrderConfirmation.jsx and pulls rows in via the shared PricingContract "Add Material/Labor"
// modal, so line items carry that screen's fields (quantity, unit/unitName, privateName, style,
// color, unitPrice, price, linePrice, gpm, lineCost, laborCategory, labor/laborName, ...).
//
// GPM convention mirrors OrderConfirmation exactly: per-line job cost is `lineCost` when present,
// otherwise derived from the entered gpm as linePrice x (100 - gpm) / 100.

const toNumber = (v) => Number(String(v ?? "").trim());
const num = (v) => {
  const n = toNumber(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round(n * 100) / 100;

// Effective unit price for a line (unitPrice, falling back to price — OrderConfirmation convention).
export function effectiveUnitPrice(item = {}) {
  const up = toNumber(item.unitPrice);
  if (!Number.isNaN(up) && String(item.unitPrice ?? "").trim() !== "") return up;
  return num(item.price);
}

// Line extended price = qty x effective unit price.
export function lineExtended(item = {}) {
  return round2(num(item.quantity) * effectiveUnitPrice(item));
}

// Return the item with linePrice recomputed (2dp string, matching the grid's display).
export function recalcLinePrice(item = {}) {
  return { ...item, linePrice: lineExtended(item).toFixed(2) };
}

// Per-line job cost. Precedence:
//   1. qty x unitCost   — product/kit rows carry a per-unit cost (currentCost); cost scales with qty
//   2. lineCost         — an explicit per-line cost
//   3. linePrice x (100 - gpm) / 100  — derive from the entered GPM (OrderConfirmation fallback)
export function lineJobCost(item = {}) {
  if (item.unitCost !== undefined && item.unitCost !== null && String(item.unitCost).trim() !== "") {
    return round2(num(item.quantity) * num(item.unitCost));
  }
  if (item.lineCost !== undefined && item.lineCost !== null && String(item.lineCost).trim() !== "") {
    return round2(num(item.lineCost));
  }
  const linePrice = String(item.linePrice ?? "").trim() !== "" ? num(item.linePrice) : lineExtended(item);
  return round2((linePrice * (100 - num(item.gpm))) / 100);
}

// Effective line price for totals: stored linePrice when present, else recomputed qty x unitPrice.
function effectiveLinePrice(item = {}) {
  return String(item.linePrice ?? "").trim() !== "" ? num(item.linePrice) : lineExtended(item);
}

// Roll up a section's items into { unitPriceTotal, linePriceTotal, cost, profit, gpm }.
export function sectionTotals(items = []) {
  const list = Array.isArray(items) ? items : [];
  const unitPriceTotal = round2(list.reduce((s, i) => s + effectiveUnitPrice(i), 0));
  const linePriceTotal = round2(list.reduce((s, i) => s + effectiveLinePrice(i), 0));
  const cost = round2(list.reduce((s, i) => s + lineJobCost(i), 0));
  const profit = round2(linePriceTotal - cost);
  const gpm = linePriceTotal > 0 ? round2((profit / linePriceTotal) * 100) : 0;
  return { unitPriceTotal, linePriceTotal, cost, profit, gpm };
}

// Validate a single line's qty + price for Save.
//   quantity  — required, numeric, > 0
//   unitPrice — required (falls back to `price`), numeric, >= 0 (a $0 line is allowed)
// Returns an object with only the failing fields set to a message ({} when the line is valid).
function validateLine(item = {}) {
  const errors = {};
  const qtyRaw = String(item.quantity ?? "").trim();
  const qty = toNumber(qtyRaw);
  if (qtyRaw === "") errors.quantity = "Qty is required";
  else if (!Number.isFinite(qty)) errors.quantity = "Qty must be a number";
  else if (qty <= 0) errors.quantity = "Qty must be greater than 0";

  const hasUnitPrice = String(item.unitPrice ?? "").trim() !== "";
  const hasPrice = String(item.price ?? "").trim() !== "";
  if (!hasUnitPrice && !hasPrice) {
    errors.unitPrice = "Price is required";
  } else {
    const priceRaw = hasUnitPrice ? String(item.unitPrice).trim() : String(item.price).trim();
    const price = toNumber(priceRaw);
    if (!Number.isFinite(price)) errors.unitPrice = "Price must be a number";
    else if (price < 0) errors.unitPrice = "Price cannot be negative";
  }
  return errors;
}

// Validate all material + labor lines. Returns
// { isValid, material: { [index]: {quantity?, unitPrice?} }, labor: {...} } — only invalid lines appear.
export function validateEstimateItems(materialList = [], laborList = []) {
  const collect = (list) => {
    const out = {};
    (Array.isArray(list) ? list : []).forEach((item, index) => {
      const errs = validateLine(item);
      if (Object.keys(errs).length) out[index] = errs;
    });
    return out;
  };
  const material = collect(materialList);
  const labor = collect(laborList);
  const isValid = Object.keys(material).length === 0 && Object.keys(labor).length === 0;
  return { isValid, material, labor };
}

// Save requires at least one material or labor line — an estimate with both grids empty has
// nothing to persist.
export function hasEstimateItems(materialList = [], laborList = []) {
  return (
    (Array.isArray(materialList) ? materialList : []).length > 0 ||
    (Array.isArray(laborList) ? laborList : []).length > 0
  );
}

// --- Add Scope de-duplication -------------------------------------------------------------------
// The "Add Scope" modal can be run repeatedly, and the same structure/product may be picked again
// in a later run (e.g. Cabinet Boxes chosen in two sessions). Identify a row by its structure +
// product/labor identity (plus name/style/color as a guard when ids are missing) so re-picking it
// updates the existing line instead of duplicating it — the LATEST selection wins.
const str = (v) => String(v ?? "").trim().toLowerCase();

export function materialIdentityKey(item = {}) {
  return [
    "m",
    num(item.scopeBlockId ?? 0),
    num(item.productStructureId),
    num(item.productId),
    num(item.materialId),
    // Keep a kit row shared by several primaries distinct per primary, so each primary's kit line
    // survives (otherwise same-materialId rows collapse, latest-wins).
    num(item.parentProductId ?? 0),
    str(item.privateName),
    str(item.style),
    str(item.color),
  ].join("|");
}

export function laborIdentityKey(item = {}) {
  return [
    "l",
    num(item.scopeBlockId ?? 0),
    num(item.productStructureId),
    num(item.laborId),
    num(item.laborItemId),
    // Keep per-primary kit labor rows distinct (see materialIdentityKey).
    num(item.parentProductId ?? 0),
    str(item.labor ?? item.laborName),
    str(item.laborCategory),
  ].join("|");
}

// Merge `incoming` rows into `existing`, keyed by identity: a row whose key already appears (in the
// existing list or earlier in the same batch) REPLACES that row in place (keeping its position);
// genuinely new rows are appended. The latest selection wins, so re-adding a product shows its
// newest values without duplicating the line.
export function upsertUnique(existing = [], incoming = [], keyFn) {
  const base = Array.isArray(existing) ? existing : [];
  const add = Array.isArray(incoming) ? incoming : [];
  const indexByKey = new Map();
  const result = [...base];
  result.forEach((item, i) => indexByKey.set(keyFn(item), i));
  add.forEach((item) => {
    const key = keyFn(item);
    if (indexByKey.has(key)) {
      const existing = result[indexByKey.get(key)];
      // Preserve a keyed quantity when the incoming item has a blank one (e.g. re-adding a product
      // from the modal returns qty="" but the estimator already entered a value).
      const incomingQty = String(item.quantity ?? "").trim();
      const existingQty = String(existing.quantity ?? "").trim();
      const merged =
        incomingQty === "" && existingQty !== ""
          ? { ...item, quantity: existing.quantity, linePrice: existing.linePrice }
          : item;
      result[indexByKey.get(key)] = merged;
    } else {
      indexByKey.set(key, result.length);
      result.push(item);
    }
  });
  return result;
}

// --- Kit quantity calculation ----------------------------------------------------------------
// When the estimator keys Qty on a kit's PARENT product row (isPrimary, from Add Scope), every
// add-on row for that kit is auto-calculated based on the master unit type:
//
//   EA master (Cabinet Boxes):
//     All kit addons (material and labor) → qty=1 (hand-entered).
//
//   LF master (Countertops):
//     Primary qty → rounded UP to the next multiple of primary.coverage
//       e.g. qty=10, coverage=8 → ceil(10/8)×8 = 16
//     All kit addons (material and labor) → qty=1 (hand-entered).
//
//   SF master (Cabinet Doors):
//     All LABOR addons  → qty = 1 (hand-entered; no EA chain in the labor side).
//     Material EA addons with coverage (Shop Supplies): ceil(masterSF / coverage)
//     Material LM/LF derived addons (Thermofoil): eaQty × coverage
//     Material "Fixtures" addons (EA without coverage, or any catch-all): qty = 1
//
// The estimator can still edit any add-on Qty after auto-calculation.
export function syncKitAddonQuantity(materialList = [], laborList = [], kitId, quantity, masterUnit = "", parentProductId, scopeBlockId, primaryQtys = null) {
  const id = num(kitId);
  if (!id) return { material: materialList, labor: laborList };

  const masterQty = num(quantity);
  const mUnit = String(masterUnit ?? "").trim().toUpperCase();
  const unitOf = (item) => String(item.unit ?? item.unitName ?? "").trim().toUpperCase();

  // Multi-primary estimates tag each kit row with the primary it belongs to (parentProductId), so a
  // kit shared by several primaries syncs INDEPENDENTLY: keying one primary only recalculates its
  // own kit rows. Match the keyed primary's rows by that link; fall back to the shared kitId for
  // legacy / single-primary rows that carry no parent (keeps existing behavior unchanged).
  const pid =
    parentProductId === undefined || parentProductId === null || parentProductId === ""
      ? null
      : num(parentProductId);
  // Only a positive parentProductId is an explicit parent link; 0 means the backend dropped it.
  const hasParent = (item) => num(item.parentProductId) > 0;
  // Undefined preserves the existing callers' behavior; null explicitly selects legacy rows.
  const inScope = (item) => scopeBlockId === undefined || num(item.scopeBlockId) === num(scopeBlockId);
  // Reliable primary detection that survives a backend round-trip where isPrimary may be lost.
  // Explicit flag wins; fall back to the legacy heuristic: a product row (materialId=0, productId>0)
  // with no parent link is a primary, not an addon.
  const isMaterialPrimary = (item) =>
    item.isPrimary === true ||
    (item.isPrimary == null &&
      num(item.materialId) === 0 &&
      num(item.productId) > 0 &&
      !num(item.parentProductId));
  const isAddon = (item) => {
    if (!inScope(item) || isMaterialPrimary(item)) return false;
    // Normal path: backend preserves kitId for material addons.
    if (num(item.kitId) === id) {
      return pid !== null && hasParent(item) ? num(item.parentProductId) === pid : true;
    }
    // Fallback for labor addons: the save API drops kitId to 0, but backfillKitParentLabels
    // restores parentProductId. Standalone labor always has parentProductId=0, so any labor
    // row with parentProductId>0 and kitId=0 is a kit addon. One kit per CNC scope block,
    // so we don't need to further scope by pid here.
    if (num(item.kitId) === 0 && hasParent(item)) return true;
    return false;
  };
  // The primary whose qty was keyed — by productId when a parent link is in play, else any primary
  // of this kit (single-primary path).
  const isKeyedPrimary = (item) =>
    inScope(item) && isMaterialPrimary(item) && num(item.kitId) === id && (pid !== null ? num(item.productId) === pid : true);

  const isFixturesAddon = (item) =>
    isAddon(item) && (item.cncProductStructure || "").toLowerCase().includes("fixture");

  // EA material addons for SF master — must have coverage > 0 AND must not be Fixtures.
  const isEaItem = (item) =>
    isAddon(item) && mUnit === "SF" && unitOf(item) === "EA" && num(item.coverage) > 0 && !isFixturesAddon(item);
  // Derived material addons (SF master only): LM/LF/etc. chained from the EA result.
  const isDerived = (item) =>
    isAddon(item) && mUnit === "SF" && !["SF", "EA"].includes(unitOf(item)) && num(item.coverage) > 0 && !isFixturesAddon(item);

  // For multi-primary SF, rule 3a: sum each primary's ceil(qty/cov) individually rather than
  // ceil(sum/cov) — the two differ whenever individual SF values aren't multiples of coverage.
  const sfEaQty = (item) => {
    const cov = num(item.coverage);
    if (Array.isArray(primaryQtys) && primaryQtys.length > 1 && cov > 0) {
      return primaryQtys.reduce((s, q) => s + Math.ceil(num(q) / cov), 0);
    }
    return cov > 0 ? Math.ceil(masterQty / cov) : masterQty;
  };

  const derivedQty = (item, allMat) => {
    const eaItem = allMat.find((t) => isEaItem(t));
    const parentQty = eaItem ? num(eaItem.quantity) : 0;
    return round2(parentQty * num(item.coverage));
  };

  // LF master: round the entered qty up to the next multiple of the primary item's coverage.
  let roundedLF = masterQty;
  if (mUnit === "LF") {
    const allItems = [
      ...(Array.isArray(materialList) ? materialList : []),
      ...(Array.isArray(laborList) ? laborList : []),
    ];
    const primaryItem = allItems.find(isKeyedPrimary);
    const primaryCov = primaryItem ? num(primaryItem.coverage) : 0;
    if (primaryCov > 0) roundedLF = Math.ceil(masterQty / primaryCov) * primaryCov;
  }

  // Pass 1 — quantity per item, dispatched by master unit + material-vs-labor.
  const applyPass1 = (item, isLabor) => {
    // LF master: update the keyed primary to the rounded increment (only the keyed one, so a
    // sibling primary sharing this kit keeps its own qty).
    if (mUnit === "LF" && isKeyedPrimary(item)) {
      return recalcLinePrice({ ...item, quantity: String(roundedLF) });
    }
    // SF master: ALL labor addons → qty=1 (hand-entered; no chain on the labor side).
    if (mUnit === "SF" && isLabor && isAddon(item)) {
      return recalcLinePrice({ ...item, quantity: "1" });
    }
    // SF master: Fixtures material addons → qty=1 regardless of unit or coverage.
    if (mUnit === "SF" && !isLabor && isFixturesAddon(item)) {
      return recalcLinePrice({ ...item, quantity: "1" });
    }
    // SF master → EA material addon with coverage (Shop Supplies): coverage-division.
    if (isEaItem(item)) return recalcLinePrice({ ...item, quantity: String(sfEaQty(item)) });
    // LF master (Countertop) and EA master (Cabinet Box): all kit addons → qty=1 (hand-entered).
    if ((mUnit === "LF" || mUnit === "EA") && isAddon(item)) {
      return recalcLinePrice({ ...item, quantity: "1" });
    }
    // SF master — non-EA / non-derived material addons (catch-all) → qty=1.
    if (mUnit === "SF" && isAddon(item) && !isDerived(item)) {
      return recalcLinePrice({ ...item, quantity: "1" });
    }
    // Any other master unit: addon → qty=1.
    if (isAddon(item) && !isDerived(item)) {
      return recalcLinePrice({ ...item, quantity: "1" });
    }
    return item;
  };
  const mat1 = (Array.isArray(materialList) ? materialList : []).map((item) => applyPass1(item, false));
  const lab1 = (Array.isArray(laborList) ? laborList : []).map((item) => applyPass1(item, true));

  // Pass 2: derive LM/LF/etc. material addons from the Pass-1 EA result (SF master only).
  // Labor is already settled in Pass 1 (qty=1 for all masters).
  const mat2 = mat1.map((item) =>
    isDerived(item) ? recalcLinePrice({ ...item, quantity: String(derivedQty(item, mat1)) }) : item
  );

  return { material: mat2, labor: lab1 };
}

// When the estimator manually edits an EA addon qty (e.g. Shop Supplies changed to 2.5),
// re-derive all LM/LF/etc. items in the same kit from the new EA value × their coverage.
export function syncDerivedFromEaAddon(materialList = [], laborList = [], kitId, eaQty, parentProductId, scopeBlockId) {
  const id = num(kitId);
  if (!id) return { material: materialList, labor: laborList };

  const eaQtyNum = num(eaQty);
  const unitOf = (item) => String(item.unit ?? item.unitName ?? "").trim().toUpperCase();
  // Scope the cascade to the edited addon's own primary when a parent link is present, so one
  // primary's EA edit doesn't re-derive a sibling primary's kit rows (fallback: shared kitId).
  const pid =
    parentProductId === undefined || parentProductId === null || parentProductId === ""
      ? null
      : num(parentProductId);
  const hasParent = (item) => num(item.parentProductId) > 0;
  const isDerived = (item) =>
    (scopeBlockId === undefined || num(item.scopeBlockId) === num(scopeBlockId)) &&
    !item.isPrimary &&
    num(item.kitId) === id &&
    (pid !== null && hasParent(item) ? num(item.parentProductId) === pid : true) &&
    !["SF", "EA"].includes(unitOf(item)) &&
    num(item.coverage) > 0;

  const recompute = (list) =>
    (Array.isArray(list) ? list : []).map((item) =>
      isDerived(item)
        ? recalcLinePrice({ ...item, quantity: String(round2(eaQtyNum * num(item.coverage))) })
        : item
    );

  return { material: recompute(materialList), labor: recompute(laborList) };
}

// Estimate total = all material + labor line prices.
export function estimateItemsTotal(materialList = [], laborList = []) {
  const mat = (Array.isArray(materialList) ? materialList : []).reduce((s, i) => s + effectiveLinePrice(i), 0);
  const lab = (Array.isArray(laborList) ? laborList : []).reduce((s, i) => s + effectiveLinePrice(i), 0);
  return round2(mat + lab);
}
